import type { AccordionTriggerProps as BaseAccordionTriggerProps } from '@radix-ui/react-accordion';
import type { DetailedHTMLProps, HTMLProps, Ref } from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { useEffect, useRef, useState } from 'react';

import { selectors } from '../../common/selectors';
import { UtilityControl } from '../../controls/UtilityControl/UtilityControl';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { Badge } from '../Badge/Badge';

const AccordionTriggerHeader = styled(AccordionPrimitive.Header, {
  all: 'unset',
  display: 'flex',
  padding: '$2 $0',
  justifyContent: 'space-between',
});
AccordionTriggerHeader.displayName = 'AccordionTriggerHeader';

const AccordingTriggerLabel = styled(Text);
AccordingTriggerLabel.displayName = 'AccordingTriggerLabel';

const AccordingTriggerContainer = styled(AccordionPrimitive.Trigger, {
  [selectors.focus]: {
    outline: 'none',
  },
});
AccordingTriggerContainer.displayName = 'AccordingTriggerContainer';

const AccordionTriggerChevron = styled(UtilityControl, {
  '[data-state=open] &': { transform: 'rotate(90deg)' },
});
AccordionTriggerChevron.displayName = 'AccordionTriggerChevron';

type AccordionTriggerProps = BaseAccordionTriggerProps & {
  count?: number;
  ref?: Ref<HTMLButtonElement | null>;
};

export function AccordionTrigger({
  children,
  count,
  ref: forwardedRef,
  ...remaining
}: AccordionTriggerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return undefined;

    const observer = new MutationObserver(() => {
      const state = trigger.getAttribute('data-state');
      setIsOpen(state === 'open');
    });

    observer.observe(trigger, { attributes: true, attributeFilter: ['data-state'] });

    const initialState = trigger.getAttribute('data-state');
    setIsOpen(initialState === 'open');

    return () => {
      observer.disconnect();
    };
  }, []);

  const setRefs = (node: HTMLButtonElement | null) => {
    triggerRef.current = node;
    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
    } else if (forwardedRef && 'current' in forwardedRef) {
      const refObject = forwardedRef as { current: HTMLButtonElement | null };
      refObject.current = node;
    }
  };

  return (
    <AccordionTriggerHeader>
      <AccordingTriggerContainer tabIndex={-1} {...remaining} ref={setRefs}>
        <AlignStack
          direction="row"
          align="center"
          gap={6}
          preset="body"
          width="fit-content"
          start={
            <AccordionTriggerChevron
              aria-hidden
              icon="chevron-right"
              label={isOpen ? 'Collapse' : 'Expand'}
              variant="neutral"
            />
          }
          end={
            count ? (
              <Badge ends="card" size="small" variant="neutral">
                {count}
              </Badge>
            ) : undefined
          }
        >
          <AccordingTriggerLabel weight="bold">{children}</AccordingTriggerLabel>
        </AlignStack>
      </AccordingTriggerContainer>
    </AccordionTriggerHeader>
  );
}

const AccordionContentContainer = styled(AccordionPrimitive.Content);
AccordionContentContainer.displayName = 'AccordionContentContainer';

export function AccordionContent({
  children,
  ref,
  ...props
}: DetailedHTMLProps<HTMLProps<HTMLDivElement>, HTMLDivElement>) {
  return (
    <AccordionContentContainer {...props} ref={ref}>
      {children}
    </AccordionContentContainer>
  );
}

export const AccordionItem = styled(AccordionPrimitive.Item, {
  display: 'flex',
  flexDirection: 'column',
  padding: '$8 $16',
  strokeBottom: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    strokeBottom: colors.strokeNeutralDark,
  },

  '&[data-state=open]': { gap: '$4' },
});
AccordionItem.displayName = 'AccordionItem';

export const Accordion = styled(AccordionPrimitive.Root, {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '$8',
  strokeAll: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    strokeAll: colors.strokeNeutralDark,
  },

  [`& ${AccordionItem}:last-child`]: {
    strokeBottom: colors.transparent,
  },
});
Accordion.displayName = 'Accordion';
